/**
 * ListPagination — second-strip footer chrome for list pages.
 *
 * Drives standard cursor-stack pagination: left side shows the
 * "Showing X–Y of N · sorted by …" caption, right side shows
 * Prev / numbered pages / Next buttons. The caller owns the cursor stack
 * (`useState<string[]>([])`) and passes the derived booleans + handlers.
 *
 * Why cursor stack instead of `useInfiniteQuery`: this matches the
 * operator's mental model of "pages" (Prev / Next / page-N) and keeps
 * each page as a separately-cached `useQuery`. `useInfiniteQuery` makes
 * sense when the UX is "Load more" (append rows) — not here.
 *
 * Numbered jump: when both `totalPages` and `onJump` are provided, the
 * indicator renders as `[1] [2] … [N]` buttons (prototype/admin/employees.html).
 * Jumping to a page beyond the cached cursor requires the caller to walk
 * forward (multiple sequential fetches) — that's the caller's responsibility
 * because only the caller knows the tRPC procedure to call.
 *
 * Used inside `<MasterListCard pagination={…}>`.
 */
import { ChevronLeft, ChevronRight } from "lucide-react";

export interface ListPaginationProps {
  /** 1-based index of the current page. */
  pageNumber: number;
  /** First row's 1-based index on this page (`(pageNumber-1) * limit + 1`). */
  showingFrom: number;
  /** Last row's 1-based index on this page. */
  showingTo: number;
  /** Total row count across all pages, when known. Renders `Showing X–Y of N`. */
  total?: number;
  /**
   * Total number of pages, when known. When paired with `onJump`, numbered
   * page buttons are rendered. Without `onJump`, the current page number is
   * shown with a `X / N` suffix.
   */
  totalPages?: number;
  /** Caption to the right of the count, e.g. `"sorted by newest first"`. */
  sortLabel: string;
  /** Whether the Prev button should be enabled (false on page 1). */
  hasPrev: boolean;
  /** Whether the Next button should be enabled (false when no nextCursor). */
  hasNext: boolean;
  onPrev: () => void;
  onNext: () => void;
  /**
   * Optional click-to-jump handler. When provided alongside `totalPages`,
   * the indicator renders numbered page buttons. The handler may be async
   * (the caller is expected to walk the cursor stack forward when the
   * target page isn't already cached).
   */
  onJump?: (target: number) => void | Promise<void>;
}

/**
 * Compress page numbers around the current page so the bar stays readable
 * for large totals. Always shows page 1 and the last page; collapses the
 * gaps with "…" sentinels.
 *
 *   pages(1, 2)  → [1, 2]
 *   pages(1, 5)  → [1, 2, 3, 4, 5]
 *   pages(1, 20) → [1, 2, "…", 20]
 *   pages(7, 20) → [1, "…", 6, 7, 8, "…", 20]
 *   pages(19, 20)→ [1, "…", 18, 19, 20]
 */
export function pageWindow(current: number, total: number): Array<number | "ellipsis"> {
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
  const out: Array<number | "ellipsis"> = [1];
  const start = Math.max(2, current - 1);
  const end = Math.min(total - 1, current + 1);
  if (start > 2) out.push("ellipsis");
  for (let p = start; p <= end; p++) out.push(p);
  if (end < total - 1) out.push("ellipsis");
  out.push(total);
  return out;
}

export function ListPagination({
  pageNumber,
  showingFrom,
  showingTo,
  total,
  totalPages,
  sortLabel,
  hasPrev,
  hasNext,
  onPrev,
  onNext,
  onJump,
}: ListPaginationProps) {
  const empty = showingTo < showingFrom;
  const showNumberedButtons = typeof totalPages === "number" && totalPages > 1 && !!onJump;

  return (
    <div className="flex flex-wrap items-center justify-between gap-3 bg-raised px-4 py-2.5 text-xs text-muted">
      <span>
        Showing{" "}
        <strong className="font-mono tabular-nums font-bold text-ink">
          {empty ? "0" : `${showingFrom}–${showingTo}`}
        </strong>
        {typeof total === "number" ? (
          <>
            {" "}
            of <strong className="font-mono tabular-nums font-bold text-ink">{total}</strong>
          </>
        ) : null}
        {" · "}
        {sortLabel}
      </span>
      <div className="flex items-center gap-1">
        <button
          type="button"
          onClick={onPrev}
          disabled={!hasPrev}
          aria-label="Previous page"
          className="grid h-7 w-7 place-items-center rounded-sm border border-line bg-paper text-muted enabled:hover:bg-bg enabled:hover:text-ink disabled:cursor-not-allowed disabled:opacity-50"
        >
          <ChevronLeft size={12} strokeWidth={2.4} />
        </button>

        {showNumberedButtons ? (
          <PageButtons
            pageNumber={pageNumber}
            totalPages={totalPages}
            onJump={onJump}
          />
        ) : (
          <CurrentPageBadge pageNumber={pageNumber} totalPages={totalPages} />
        )}

        <button
          type="button"
          onClick={onNext}
          disabled={!hasNext}
          aria-label="Next page"
          className="grid h-7 w-7 place-items-center rounded-sm border border-line bg-paper text-muted enabled:hover:bg-bg enabled:hover:text-ink disabled:cursor-not-allowed disabled:opacity-50"
        >
          <ChevronRight size={12} strokeWidth={2.4} />
        </button>
      </div>
    </div>
  );
}

function PageButtons({
  pageNumber,
  totalPages,
  onJump,
}: {
  pageNumber: number;
  totalPages: number;
  onJump: (target: number) => void | Promise<void>;
}) {
  const items = pageWindow(pageNumber, totalPages);
  return (
    <>
      {items.map((item, i) =>
        item === "ellipsis" ? (
          <span
            key={`ellipsis-${i}`}
            aria-hidden
            className="grid h-7 min-w-[20px] place-items-center px-1 text-muted"
          >
            …
          </span>
        ) : item === pageNumber ? (
          <span
            key={item}
            aria-current="page"
            aria-label={`Page ${item} of ${totalPages}`}
            className="grid h-7 min-w-[28px] place-items-center rounded-sm border border-sky-600 bg-sky-600 px-2 text-xs font-semibold text-white tabular-nums"
          >
            {item}
          </span>
        ) : (
          <button
            key={item}
            type="button"
            onClick={() => void onJump(item)}
            aria-label={`Go to page ${item}`}
            className="grid h-7 min-w-[28px] place-items-center rounded-sm border border-line bg-paper px-2 text-xs font-medium text-ink-2 tabular-nums hover:bg-bg hover:text-ink"
          >
            {item}
          </button>
        ),
      )}
    </>
  );
}

function CurrentPageBadge({
  pageNumber,
  totalPages,
}: {
  pageNumber: number;
  totalPages: number | undefined;
}) {
  return (
    <span
      aria-current="page"
      aria-label={
        typeof totalPages === "number" ? `Page ${pageNumber} of ${totalPages}` : `Page ${pageNumber}`
      }
      className="grid h-7 min-w-[28px] place-items-center rounded-sm border border-sky-600 bg-sky-600 px-2 text-xs font-semibold text-white tabular-nums"
    >
      {typeof totalPages === "number" ? (
        <>
          {pageNumber}
          <span className="font-normal text-white/70">&nbsp;/&nbsp;{totalPages}</span>
        </>
      ) : (
        pageNumber
      )}
    </span>
  );
}
